"use client";

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { usePathname } from 'next/navigation';
import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { STORIES_BASE_PATH } from "@/constants/routes";


const AppContext = createContext()

// Helper to create a promise with an exposed resolve function
function createResolvablePromise() {
  let resolveFunc
  const promise = new Promise((resolve) => {
    resolveFunc = resolve
  })
  return Object.assign(promise, { resolve: resolveFunc })
}

export function AppProvider({ children }) {
  const [recordCategoryType, setRecordCategoryType] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const [isRecordsNavOverlayOpen, setIsRecordsNavOverlayOpen] = useState(false);
  const [underlineSize, setUnderlineSize] = useState("small");
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const transitionPromiseRef = useRef(createResolvablePromise());
  const timeoutIdRef = useRef(null);
  const pathname = usePathname();
  const [initialPathName] = useState(pathname);
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const [previousStoryListingRoute, setPreviousStoryListingRoute] =
    useState(null);
  const [previousRouteBeforeMap, setPreviousListingRouteBeforeMap] =
    useState(null);
  const previousStoryListingRouteRef = useRef(null);
  const [previousStoryColor, setPreviousStoryColor] = useState(null);
  const [currentTransitionName, setCurrentTransitionName] = useState(null);
  const savedScrollPositionRef = useRef(null);
  const savedFocusHrefRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [aboutAnimationCompleted, setAboutAnimationCompleted] = useState(false);

  // Update during render - valid React pattern for derived state
  if (isInitialLoad && pathname !== initialPathName) {
    setIsInitialLoad(false);
  }

  const [previousRoute, setPreviousRoute] = useState(null);
  const [previousMapRoute, setPreviousMapRoute] = useState(null);

  const navLinks = [
    {
      label: "About",
      href: `${STORIES_BASE_PATH}`,
      underlineSize: "small",
      overlayUnderlineScale: 1,
      desktopHeaderUnderlineScale: 1,
      headerUnderlineScale: 0.67,
      headerUnderlineOrigin: "5% 50%",
    },
    {
      label: "Record Gallery",
      href: `${STORIES_BASE_PATH}/record-gallery`,
      underlineSize: "medium",
      overlayUnderlineScale: 1.25,
      desktopHeaderUnderlineScale: 1.3,
      headerUnderlineScale: 0.87,
      headerUnderlineOrigin: "0% 50%",
    },
    {
      label: "250 Stories",
      href: `${STORIES_BASE_PATH}/250-stories`,
      underlineSize: "medium",
      overlayUnderlineScale: 1,
      desktopHeaderUnderlineScale: 1,
      headerUnderlineScale: 0.67,
      headerUnderlineOrigin: "30% 50%",
    },
    //
  ];

  // Create the ready promise only once using useMemo
  const ready = useMemo(() => createResolvablePromise(), []);

  // Getter function to access the current transition promise
  const getTransitionReady = useMemo(() => {
    return () => transitionPromiseRef.current;
  }, []);

  // Handle route change start (called from AnimatedLink onClick)
  const handleRouteChangeStart = useCallback((transitionName, href) => {
    // Save scroll position and focus target when entering an overlay
    if (transitionName === "overlay-in") {
      savedScrollPositionRef.current = window.scrollY;
      savedFocusHrefRef.current = href;
    }
    setCurrentTransitionName(transitionName);
    setIsRouteChanging(true);
    transitionPromiseRef.current = createResolvablePromise();
  }, []);

  // Get and clear saved scroll position (for restoring after overlay closes)
  const getSavedScrollPosition = useCallback(() => {
    const position = savedScrollPositionRef.current;
    savedScrollPositionRef.current = null;
    return position;
  }, []);

  // Get and clear saved focus href (for restoring focus after overlay closes)
  const getSavedFocusHref = useCallback(() => {
    const href = savedFocusHrefRef.current;
    savedFocusHrefRef.current = null;
    return href;
  }, []);

  // Track previous story listing route (only ${STORIES_BASE_PATH}/250-stories, ${STORIES_BASE_PATH}/record-gallery/, or ${STORIES_BASE_PATH}/record-gallery/[category])
  // Track previous route
  useEffect(() => {
    return () => {
      setPreviousRoute(pathname);
    };
  }, [pathname]);

  // Track the route before entering the map (skip record detail pages so returning from a story preserves the original pre-map route)
  useEffect(() => {
    if (pathname === `${STORIES_BASE_PATH}/map`) return;
    const isRecordDetailPage =
      pathname.startsWith(`${STORIES_BASE_PATH}/record-gallery/`) &&
      pathname.split("/").length > 4;
    if (isRecordDetailPage) return;

    return () => {
      setPreviousMapRoute(pathname);
    };
  }, [pathname]);

  // Track previous story listing route (only /stories-of-us/250-stories, /stories-of-us/record-gallery/, or /stories-of-us/record-gallery/[category])
  previousStoryListingRouteRef.current = previousStoryListingRoute;

  useEffect(() => {
    const isStoryListingRoute =
      pathname === STORIES_BASE_PATH ||
      pathname === `${STORIES_BASE_PATH}/250-stories` ||
      pathname === `${STORIES_BASE_PATH}/record-gallery` ||
      pathname === `${STORIES_BASE_PATH}/map` ||
      (pathname.startsWith(`${STORIES_BASE_PATH}/record-gallery`) &&
        pathname.split("/").length === 4);

    return () => {
      if (isStoryListingRoute) {
        setPreviousStoryListingRoute(pathname);
      }
    };
  }, [pathname]);

  // const previousRouteBeforeMapRef = useRef(null);
  useEffect(() => {
    // console.log(previousRouteBeforeMapRef.current, pathname)
    const isMapRoute = pathname === `${STORIES_BASE_PATH}/map`;
    const isRecordRoute =
      pathname.startsWith(`${STORIES_BASE_PATH}/record-gallery`) &&
      pathname.split("/").length === 5;

    if (!isMapRoute && !isRecordRoute) {
      setPreviousListingRouteBeforeMap(pathname);
    }
  }, [pathname]);

  const startTransitionTimeout = (delay, cb) => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      cb && cb();
      setIsRouteChanging(false);
      setCurrentTransitionName(null);
      transitionPromiseRef.current.resolve();
    }, delay);

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  };

  // Handle route change end (triggered by pathname change)
  useEffect(() => {
    const delay = ["slide-left", "slide-right"].includes(currentTransitionName)
      ? 500
      : 0;
    return startTransitionTimeout(delay);
  }, [pathname]);

  // Resolve initial transition promise on mount
  useEffect(() => {
    return startTransitionTimeout(0);
  }, []);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1024px)", () => {
      setIsMobile(false);
    });

    mm.add("(max-width: 1023px)", () => {
      setIsMobile(true);
    });
  }, []);

  const updateCSSHeightVar = () => {
    document.documentElement.style.setProperty(
      "--vh",
      `${window.innerHeight * 0.01}px`,
    );
  };

  // Handle window resize and header height
  useEffect(() => {
    const updateSize = () => {
      const wrapperEl =
        document.documentElement.querySelector(".stories-of-us-app");
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      // Get header height from CSS variable
      const headerHeightValue = getComputedStyle(wrapperEl)
        .getPropertyValue("--header-height")
        .trim();

      // Convert rem/px to pixels
      let heightInPixels = 0;
      if (headerHeightValue.endsWith("rem")) {
        const remValue = parseFloat(headerHeightValue);
        const rootFontSize = parseFloat(getComputedStyle(wrapperEl).fontSize);
        heightInPixels = remValue * rootFontSize;
      } else if (headerHeightValue.endsWith("px")) {
        heightInPixels = parseFloat(headerHeightValue);
      }

      heightInPixels += 56;

      setHeaderHeight(heightInPixels);
    };

    // Initial update
    updateSize();

    // Check if device supports touch (mobile/tablet)
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    window.addEventListener("resize", updateCSSHeightVar);

    if (isTouchDevice) {
      // For touch devices, listen to orientation change
      const orientationQuery = window.matchMedia("(orientation: portrait)");
      const handleOrientationChange = () => {
        updateSize();
      };

      orientationQuery.addEventListener("change", handleOrientationChange);

      return () => {
        window.removeEventListener("resize", updateCSSHeightVar);
        orientationQuery.removeEventListener("change", handleOrientationChange);
      };
    } else {
      // For non-touch devices, use resize event
      window.addEventListener("resize", updateSize);

      return () => {
        window.removeEventListener("resize", updateCSSHeightVar);
        window.removeEventListener("resize", updateSize);
      };
    }
  }, []);

  useEffect(() => {
    // Load fonts and other resources
    const loadResources = async () => {
      try {
        // Wait for fonts to be ready
        await document.fonts.ready;

        // Add any other loading logic here (images, data, etc.)

        // Set app as ready
        setIsAppReady(true);
        ready.resolve();
      } catch (error) {
        console.error("Error loading resources:", error);
        // Still set as ready to prevent blocking the app
        setIsAppReady(true);
        ready.resolve();
      }
    };

    loadResources();
  }, [ready]);

  return (
    <AppContext.Provider
      value={{
        isAppReady,
        isInitialLoad,
        recordCategoryType,
        setRecordCategoryType,
        ready,
        getTransitionReady,
        handleRouteChangeStart,
        windowSize,
        headerHeight,
        isRouteChanging,
        setIsNavHidden,
        isNavHidden,
        isMobile,
        previousStoryListingRoute,
        previousRouteBeforeMap,
        previousStoryColor,
        setPreviousStoryColor,
        currentTransitionName,
        getSavedScrollPosition,
        getSavedFocusHref,
        isMobileNavOpen,
        setIsMobileNavOpen,
        underlineSize,
        setUnderlineSize,
        navLinks,
        previousRoute,
        previousMapRoute,
        aboutAnimationCompleted,
        setAboutAnimationCompleted,
        isRecordsNavOverlayOpen,
        setIsRecordsNavOverlayOpen,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
